import { canManageThreatProtection } from '@/features/threat-protection/access';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import type { AppMenuItem } from '@workspace/ui';
import { UserProfileMenu, buildMenuLookup, resolveMenuItemsFromLookup, toast } from '@workspace/ui';
import {
  ContentFrame,
  DashboardShell,
  PortalShellCommandPalette,
  PortalShellQuickActions,
  PortalShellScopeSelector,
  PortalShellSearchTrigger,
  SidebarNav,
  TopBar,
  type DashboardShellAction,
  type DashboardShellFooterAction,
  type DashboardShellNavItem,
  type DashboardShellNavSection,
  type PortalShellCommandGroup,
  type PortalShellQuickActionItem,
  type PortalShellScopeOption
} from '@workspace/silk-ui';
import { useTourAnchor } from '@workspace/tour';
import { useScope } from '@workspace/scope-context';
import {
  ChevronRight,
  Menu,
  Route,
  Star,
  StarOff,
  ShoppingBag,
  CreditCard,
  BookOpen,
  Send,
  DollarSign,
  UserCheck,
  Server,
  Key,
  Terminal,
  Map,
  Database,
  Palette,
  Compass,
  House,
  Image,
  MessageCircle,
  Type,
  TrendingUp,
  FileText,
  ShoppingCart,
  Settings,
  LogOut,
  Receipt,
  Ticket,
  Briefcase,
  ShieldCheck,
  Package,
  Percent,
  Activity,
  Network,
  GitMerge,
  Grid2x2,
  Cpu,
  Zap,
  MapPin,
  Lock,
  Layers,
  Users,
  Wifi,
  Link
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@workspace/ui';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { buildMenuConfig, type MenuItem } from '@/config/navigation';
import { useNavigation } from '@/context/NavigationContext';
import { usePreferences } from '@/context/PreferencesContext';
import { useSession } from '@/context/SessionContext';
import { pruneMenuConfig } from '@/lib/navigation/pruneMenuConfig';
import {
  buildPortalHelpOverlayRoute,
  buildPortalHelpRoute,
  portalHelpSearchTerms,
  resolveContextualPortalHelpArticleId,
} from '@/lib/portal-help';
import { useApiGatewayAccess } from '@/lib/useApiGatewayAccess';
import { useCommerceAccess } from '@/lib/useCommerceAccess';
import { useMappingAccess } from '@/lib/useMappingAccess';
import { useNotificationAccess } from '@/lib/useNotificationAccess';
import { usePaymentsAccess } from '@/lib/usePaymentsAccess';
import { useTenantUsersAccess } from '@/lib/useTenantUsersAccess';
import { resolveDashboardUrl } from '@/lib/publicUrls';
import { useStatusInventoryAccess } from '@/lib/useStatusInventoryAccess';
import { usePortalManualTour } from '@/tours/portalTours';
import { getPortalHelpArticle, getPortalHelpArticles } from '@/views/help/portalHelpContent';

interface AppLayoutProps {
  path: string;
  children: ReactNode;
  footer?: ReactNode;
}

type FlattenedMenuItem = AppMenuItem & { parentLabel?: string | null };

const FAVORITE_ACTION_ID = 'toggle-favorite';
const HELP_ACTION_ID = 'open-help';
const TOUR_ACTION_ID = 'start-tour';
const GLOBAL_SCOPE_ID = 'global';

export default function AppLayout({
  path,
  children,
  footer,
}: AppLayoutProps) {
  const navigation = useNavigation();
  const navigateTo = navigation.navigate;
  const { session, logout } = useSession();
  const { navigation: navigationPrefs, updateNavigation } = usePreferences();
  const {
    activeOrganization,
    mode,
    activeTenant,
    allowGlobal,
    organizations,
    loading: scopeLoading,
    selectTenant,
    selectGlobal
  } = useScope();
  const dashboardUrl = useMemo(
    () =>
      resolveDashboardUrl(
        typeof window !== 'undefined' ? window.location.href : 'http://localhost:5180/',
        import.meta.env?.VITE_DASHBOARD_URL as string | undefined
      ) ?? 'http://localhost:5174',
    []
  );
  const mappingAccess = useMappingAccess();
  const commerceAccess = useCommerceAccess();
  const apiGatewayAccess = useApiGatewayAccess();
  const notificationAccess = useNotificationAccess();
  const paymentsAccess = usePaymentsAccess();
  const statusInventoryAccess = useStatusInventoryAccess();
  const tenantUsersAccess = useTenantUsersAccess();
  const brandAnchor = useTourAnchor<HTMLDivElement>('portal.brand');
  const quickActionsAnchor = useTourAnchor<HTMLDivElement>('portal.quickActions');
  const contentAnchor = useTourAnchor<HTMLDivElement>('portal.workspace');
  const commandAnchor = useTourAnchor<HTMLDivElement>('portal.command');

  const rawCurrentPath = useMemo(() => navigation.path || path, [navigation.path, path]);
  const currentPath = useMemo(() => rawCurrentPath.split('?')[0] || '/', [rawCurrentPath]);
  const manualTour = usePortalManualTour(currentPath);
  const helpArticles = useMemo(() => getPortalHelpArticles(), []);
  const contextualHelpArticle = useMemo(
    () => getPortalHelpArticle(resolveContextualPortalHelpArticleId(rawCurrentPath)),
    [rawCurrentPath]
  );
  const organizationBrand = activeOrganization?.branding;
  const brandTitle = organizationBrand?.wordmark?.trim() || 'ACD';
  const brandSubtitle = 'Tenant Portal';
  const brandLogoUrl = organizationBrand?.logoUrl ?? null;
  const brandMonogram = useMemo(() => {
    const fallback = brandTitle.trim().slice(0, 2).toUpperCase();
    return brandTitle === 'ACD' ? 'ACD' : fallback || 'ACD';
  }, [brandTitle]);

  const featureMap = mappingAccess.featureMap;
  const mappingAccessEnabled = mappingAccess.mappingFeatureEnabled;
  const salesAccessEnabled = !!featureMap?.['sales.runtime.enabled'];
  const canAccessDashboard = useMemo(() => {
    const scopes = new Set(session?.scopes ?? []);
    return scopes.has('platform.manage') || scopes.has('platform.read') || scopes.has('platform.tenants.manage');
  }, [session?.scopes]);
  const scopedMenuConfig = useMemo(() => buildMenuConfig(session?.scopes), [session?.scopes]);

  const menu = useMemo(
    () =>
      pruneMenuConfig(scopedMenuConfig, {
        apiGatewayFeatureEnabled: apiGatewayAccess.apiGatewayFeatureEnabled,
        commerceFeatureEnabled: commerceAccess.commerceFeatureEnabled,
        mappingAccessEnabled,
        notificationsAccessEnabled: notificationAccess.accessEnabled,
        paymentsAccessEnabled: paymentsAccess.accessEnabled,
        resellerFeatureEnabled: commerceAccess.resellerFeatureEnabled,
        statusAccessEnabled: statusInventoryAccess.accessEnabled,
        threatProtectionAccessEnabled: canManageThreatProtection(session, activeTenant?.id ?? ''),
        salesAccessEnabled,
        tenantUsersAccessEnabled: tenantUsersAccess.accessEnabled
      }),
    [
      apiGatewayAccess.apiGatewayFeatureEnabled,
      commerceAccess.commerceFeatureEnabled,
      commerceAccess.resellerFeatureEnabled,
      mappingAccessEnabled,
      notificationAccess.accessEnabled,
      paymentsAccess.accessEnabled,
      salesAccessEnabled,
      statusInventoryAccess.accessEnabled,
      session,
      activeTenant?.id,
      scopedMenuConfig,
      tenantUsersAccess.accessEnabled
    ]
  );
  const menuLookup = useMemo(() => buildMenuLookup(menu), [menu]);

  const availableFavoriteIds = useMemo(
    () => navigationPrefs.favorites.filter(id => menuLookup.has(id)),
    [menuLookup, navigationPrefs.favorites]
  );
  const availableRecentIds = useMemo(
    () => navigationPrefs.recents.filter(id => menuLookup.has(id)),
    [menuLookup, navigationPrefs.recents]
  );

  const favoriteItems = useMemo(() => {
    const items = resolveMenuItemsFromLookup(menuLookup, availableFavoriteIds);
    if (navigationPrefs.favoritesOrder === 'alpha') {
      return [...items].sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }));
    }
    return items;
  }, [availableFavoriteIds, menuLookup, navigationPrefs.favoritesOrder]);
  const recentItems = useMemo(
    () => resolveMenuItemsFromLookup(menuLookup, availableRecentIds),
    [availableRecentIds, menuLookup]
  );

  const flattenedVisibleItems = useMemo(
    () => flattenMenuItems([...(menu.sideMenu ?? []), ...(menu.topMenu ?? []), ...(menu.userMenu ?? [])]),
    [menu]
  );
  const currentMenuItem = useMemo(
    () => findBestMenuMatch(flattenedVisibleItems, currentPath),
    [currentPath, flattenedVisibleItems]
  );
  const currentItemPinned = Boolean(currentMenuItem && navigationPrefs.favorites.includes(currentMenuItem.id));

  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [switchingScopeId, setSwitchingScopeId] = useState<string | null>(null);
  const [titleOverride, setTitleOverride] = useState<{ title: string; context?: string } | null>(null);

  useEffect(() => {
    const handleTitleChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setTitleOverride(detail);
    };
    window.addEventListener('silk-worksurface-title-change', handleTitleChange);
    return () => {
      window.removeEventListener('silk-worksurface-title-change', handleTitleChange);
    };
  }, [currentPath]);

  const onNavigate = useCallback(
    (next: string) => {
      navigateTo(next);
    },
    [navigateTo]
  );

  const handleCommandOpenChange = useCallback((next: boolean) => {
    setCommandOpen(next);
    if (!next) {
      setCommandQuery('');
    }
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || target?.getAttribute('contenteditable') === 'true';
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        handleCommandOpenChange(true);
        return;
      }
      if (!isTyping && !event.metaKey && !event.ctrlKey && event.key === '/') {
        event.preventDefault();
        handleCommandOpenChange(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleCommandOpenChange]);

  const handleRecordRecent = useCallback(
    (itemId: string) => {
      if (!itemId || itemId === 'logout') {
        return;
      }
      const trimmed = navigationPrefs.recents.filter(id => id !== itemId);
      const next = [itemId, ...trimmed].slice(0, 8);
      void updateNavigation({ recents: next });
    },
    [navigationPrefs.recents, updateNavigation]
  );

  const handleToggleFavorite = useCallback(
    (itemId: string, pinned: boolean) => {
      const next = pinned
        ? navigationPrefs.favorites.filter(id => id !== itemId)
        : [itemId, ...navigationPrefs.favorites.filter(id => id !== itemId)];
      void updateNavigation({ favorites: next });
    },
    [navigationPrefs.favorites, updateNavigation]
  );

  const handleMenuNavigateById = useCallback(
    (itemId: string) => {
      const item = menuLookup.get(itemId);
      if (!item?.route) {
        return;
      }
      onNavigate(item.route);
      handleRecordRecent(item.id);
      setMobileNavOpen(false);
    },
    [handleRecordRecent, menuLookup, onNavigate]
  );

  const handleOpenDashboard = useCallback(() => {
    if (typeof window === 'undefined' || !dashboardUrl) {
      return;
    }
    const entryUrl = new URL(dashboardUrl);
    entryUrl.searchParams.set('entry', 'portal');
    window.open(entryUrl.toString(), '_blank', 'noopener,noreferrer');
  }, [dashboardUrl]);

  const profileMenuContent = useCallback(
    (close: () => void) => (
      <div className="space-y-4">
        <Button
          variant="secondary"
          className="w-full"
          disabled={!manualTour.canStart}
          title={manualTour.canStart ? 'Start this page tour' : 'No tour for this page'}
          onClick={() => {
            close();
            manualTour.start();
          }}
        >
          Tour this page
        </Button>
        {canAccessDashboard ? (
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              close();
              handleOpenDashboard();
            }}
          >
            Open Admin Dashboard
          </Button>
        ) : null}
      </div>
    ),
    [canAccessDashboard, handleOpenDashboard, manualTour]
  );

  const quickActionItems = useMemo<PortalShellQuickActionItem[]>(
    () => [
      {
        id: 'open-support',
        label: 'Open support',
        description: 'Open the tenant-native support stub and current guidance.',
        icon: renderMenuIcon('message-circle')
      },
      {
        id: 'open-help',
        label: 'Browse help',
        description: 'Search help articles and workspace guidance.',
        icon: renderMenuIcon('book-open')
      },
      {
        id: 'open-setup',
        label: 'Continue setup',
        description: 'Review tenant onboarding and follow-up tasks.',
        icon: renderMenuIcon('zap')
      }
    ],
    []
  );

  const handleQuickAction = useCallback(
    (actionId: string) => {
      if (actionId === 'open-support') {
        navigateTo('/support');
        return;
      }
      if (actionId === 'open-help') {
        navigateTo('/help');
        return;
      }
      if (actionId === 'open-setup') {
        navigateTo('/setup');
      }
    },
    [navigateTo]
  );

  // Mobile bottom nav: Home + up to 3 available workspaces + More
  const mobileBottomNavItems = useMemo(() => {
    const items: Array<{ id: string; label: string; icon: React.ComponentType<{ className?: string }>; route: string }> = [
      { id: 'home', label: 'Home', icon: House, route: '/' },
    ];
    if (commerceAccess.commerceFeatureEnabled || commerceAccess.resellerFeatureEnabled) {
      items.push({
        id: 'commerce',
        label: 'Commerce',
        icon: ShoppingBag,
        route: commerceAccess.commerceFeatureEnabled ? '/commerce/catalogs' : '/reseller/orders',
      });
    }
    if (mappingAccess.mappingFeatureEnabled) {
      items.push({ id: 'maps', label: 'Maps', icon: Map, route: '/maps/sources' });
    }
    if (statusInventoryAccess.accessEnabled) {
      items.push({ id: 'status', label: 'Status', icon: Activity, route: '/status/inventory/devices' });
    } else if (notificationAccess.accessEnabled) {
      items.push({ id: 'notifications', label: 'Notify', icon: Zap, route: '/notifications/channels' });
    }
    // Cap at 4 workspace slots so Menu ("More") always fits in 5-item bar
    return items.slice(0, 4);
  }, [
    commerceAccess.commerceFeatureEnabled,
    commerceAccess.resellerFeatureEnabled,
    mappingAccess.mappingFeatureEnabled,
    notificationAccess.accessEnabled,
    statusInventoryAccess.accessEnabled,
  ]);

  const scopeOptions = useMemo<PortalShellScopeOption[]>(() => {
    const options: PortalShellScopeOption[] = [];
    const seen = new Set<string>();
    const tenants = (organizations ?? [])
      .flatMap(org => org.tenants ?? [])
      .filter((tenant): tenant is NonNullable<(typeof organizations)[number]['tenants']>[number] => {
        if (!tenant) {
          return false;
        }
        const key = tenant.id ?? tenant.slug ?? tenant.name ?? '';
        return Boolean(key) && !seen.has(key) && (seen.add(key), true);
      });

    if (activeTenant) {
      const activeKey = activeTenant.id ?? activeTenant.slug ?? activeTenant.name ?? '';
      if (activeKey && !seen.has(activeKey)) {
        tenants.unshift(activeTenant);
      }
    }

    if (allowGlobal) {
      options.push({
        id: GLOBAL_SCOPE_ID,
        label: 'Global scope',
        description: 'Use global account context.',
        active: mode === 'global',
        disabled: mode === 'global',
        loading: switchingScopeId === GLOBAL_SCOPE_ID,
        kind: 'global'
      });
    }

    tenants.forEach(tenant => {
      const optionId = tenant.id ?? tenant.slug ?? tenant.name ?? '';
      if (!optionId) {
        return;
      }
      options.push({
        id: optionId,
        label: tenant.name ?? tenant.id ?? 'Tenant',
        description: tenant.slug ?? tenant.id ?? null,
        active: mode === 'tenant' && tenant.id === activeTenant?.id,
        disabled: !tenant.id || (mode === 'tenant' && tenant.id === activeTenant?.id),
        loading: Boolean(tenant.id && switchingScopeId === tenant.id),
        kind: 'tenant'
      });
    });

    return options;
  }, [activeTenant, allowGlobal, mode, organizations, switchingScopeId]);

  const scopeTenantCount = useMemo(
    () => new Set(scopeOptions.filter(option => option.kind === 'tenant').map(option => option.id)).size,
    [scopeOptions]
  );
  const scopeTriggerLabel = mode === 'global' ? 'Global scope' : activeTenant?.name ?? 'Select tenant';
  const scopeTriggerBadgeLabel = scopeTenantCount > 1 || allowGlobal ? 'Tenant' : null;

  const handleScopeSelect = useCallback(
    async (optionId: string) => {
      if (!optionId) {
        return;
      }

      if (optionId === GLOBAL_SCOPE_ID) {
        if (mode === 'global') {
          return;
        }
        setSwitchingScopeId(GLOBAL_SCOPE_ID);
        try {
          await selectGlobal();
        } catch (error) {
          toast.error(getScopeErrorMessage(error, 'Failed to switch scope.'));
        } finally {
          setSwitchingScopeId(null);
        }
        return;
      }

      if (!activeTenant?.id || optionId !== activeTenant.id) {
        setSwitchingScopeId(optionId);
        try {
          await selectTenant(optionId);
        } catch (error) {
          toast.error(getScopeErrorMessage(error, 'Failed to switch tenant.'));
        } finally {
          setSwitchingScopeId(null);
        }
      }
    },
    [activeTenant?.id, mode, selectGlobal, selectTenant]
  );

  const sidebarSections = useMemo(
    () => buildSidebarSections(menu.sideMenu ?? [], favoriteItems, currentPath),
    [currentPath, favoriteItems, menu.sideMenu]
  );
  const sidebarFooterItems = useMemo(
    () => buildSidebarFooterItems(menu.sideMenu ?? [], currentPath),
    [currentPath, menu.sideMenu]
  );

  const commandGroups = useMemo<PortalShellCommandGroup[]>(() => {
    const query = commandQuery.trim().toLowerCase();
    const groups: PortalShellCommandGroup[] = [];

    if (!query && quickActionItems.length > 0) {
      groups.push({
        id: 'actions',
        heading: 'Actions',
        items: quickActionItems.map(action => ({
          id: `action:${action.id}`,
          label: action.label,
          keywords: action.description ? [action.description] : [],
          disabled: action.disabled
        }))
      });
    }

    if (!query && recentItems.length > 0) {
      groups.push({
        id: 'recent',
        heading: 'Recent',
        items: recentItems
          .filter(item => item.route)
          .map(item => ({
            id: `recent:${item.id}`,
            label: item.label,
            keywords: [item.description ?? '', item.route ?? '']
          }))
      });
    }

    const helpItems = helpArticles
      .filter(article => {
        if (!query) {
          return (
            article.articleId === contextualHelpArticle?.articleId ||
            article.articleId === 'getting-started' ||
            article.articleId === 'tenant-context'
          );
        }
        return portalHelpSearchTerms(article).join(' ').toLowerCase().includes(query);
      })
      .slice(0, query ? 8 : 3)
      .map(article => ({
        id: `help:${article.articleId}`,
        label: article.title,
        keywords: [article.summary, ...(article.relatedRoutes.slice(0, 1))],
      }));

    if (helpItems.length > 0) {
      groups.push({
        id: 'help',
        heading: query ? 'Help' : 'Help Topics',
        items: helpItems,
      });
    }

    const navItems = flattenMenuItems([...(menu.sideMenu ?? []), ...(menu.topMenu ?? []), ...(menu.userMenu ?? [])])
      .filter(item => item.route)
      .filter(item => {
        if (!query) {
          return true;
        }
        const haystack = [item.label, item.description ?? '', item.route ?? ''].join(' ').toLowerCase();
        return haystack.includes(query);
      })
      .map(item => ({
        id: `nav:${item.id}`,
        label: item.label,
        keywords: [item.description ?? '', item.route ?? '']
      }));

    if (navItems.length > 0) {
      groups.push({
        id: query ? 'results' : 'pages',
        heading: query ? 'Results' : 'Pages',
        items: navItems
      });
    }

    return groups;
  }, [commandQuery, contextualHelpArticle?.articleId, helpArticles, menu, quickActionItems, recentItems]);

  const handleCommandSelect = useCallback(
    (itemId: string) => {
      if (itemId.startsWith('action:')) {
        handleQuickAction(itemId.slice('action:'.length));
        handleCommandOpenChange(false);
        return;
      }
      if (itemId.startsWith('help:')) {
        onNavigate(buildPortalHelpRoute(itemId.slice('help:'.length)));
        handleCommandOpenChange(false);
        return;
      }

      const menuItemId = itemId.replace(/^recent:/, '').replace(/^nav:/, '');
      const item = menuLookup.get(menuItemId);
      if (item?.route) {
        onNavigate(item.route);
        handleRecordRecent(item.id);
      }
      handleCommandOpenChange(false);
    },
    [handleCommandOpenChange, handleQuickAction, handleRecordRecent, menuLookup, onNavigate]
  );

  const avatarLabel = useMemo(() => buildInitials(session?.username ?? session?.email ?? ''), [session?.email, session?.username]);
  const topBarActions = useMemo<DashboardShellAction[]>(
    () => [
      ...(currentMenuItem
        ? [
            {
              id: FAVORITE_ACTION_ID,
              label: currentItemPinned ? 'Unfavorite' : 'Favorite',
              icon: currentItemPinned ? <StarOff className="h-4 w-4" aria-hidden /> : <Star className="h-4 w-4" aria-hidden />
            }
          ]
        : []),
      {
        id: HELP_ACTION_ID,
        label: contextualHelpArticle ? `Help: ${contextualHelpArticle.title}` : 'Help',
        icon: <BookOpen className="h-4 w-4" aria-hidden />,
      },
      {
        id: TOUR_ACTION_ID,
        label: 'Start page tour',
        icon: <Route className="h-4 w-4" aria-hidden />,
        disabled: !manualTour.canStart
      }
    ],
    [contextualHelpArticle, currentItemPinned, currentMenuItem, manualTour.canStart]
  );

  const handleTopBarAction = useCallback(
    (actionId: string) => {
      if (actionId === FAVORITE_ACTION_ID && currentMenuItem) {
        handleToggleFavorite(currentMenuItem.id, currentItemPinned);
        return;
      }
      if (actionId === HELP_ACTION_ID) {
        if (contextualHelpArticle && !currentPath.startsWith('/help')) {
          navigateTo(buildPortalHelpOverlayRoute(contextualHelpArticle.articleId, rawCurrentPath), { replace: true });
          return;
        }
        navigateTo(buildPortalHelpRoute(contextualHelpArticle?.articleId));
        return;
      }
      if (actionId === TOUR_ACTION_ID && manualTour.canStart) {
        manualTour.start();
      }
    },
    [contextualHelpArticle, currentItemPinned, currentMenuItem, currentPath, handleToggleFavorite, manualTour, navigateTo, rawCurrentPath]
  );

  const brand = useMemo(
    () => ({
      title: brandTitle,
      subtitle: brandSubtitle,
      icon: brandLogoUrl ? <img src={brandLogoUrl} alt={`${brandTitle} logo`} className="h-full w-full object-contain" /> : undefined,
      monogram: brandMonogram,
      hrefLabel: 'Portal home'
    }),
    [brandLogoUrl, brandMonogram, brandSubtitle, brandTitle]
  );

  const handleToggleCompact = useCallback(() => {
    updateNavigation({ collapsed: !navigationPrefs.collapsed });
  }, [navigationPrefs.collapsed, updateNavigation]);

  const mobileBottomBar = (
    <nav
      aria-label="Mobile navigation"
      className="flex items-stretch border-t border-border bg-card/95 backdrop-blur-sm"
    >
      {mobileBottomNavItems.map(item => {
        const Icon = item.icon;
        const isActive =
          item.route === '/'
            ? currentPath === '/'
            : currentPath === item.route || currentPath.startsWith(`${item.route}/`);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => navigateTo(item.route)}
            className={cn(
              'flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium transition-colors',
              isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className="h-5 w-5" aria-hidden />
            <span>{item.label}</span>
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => setMobileNavOpen(true)}
        className="flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium text-muted-foreground transition-colors hover:text-foreground"
        aria-label="Open full navigation menu"
      >
        <Menu className="h-5 w-5" aria-hidden />
        <span>More</span>
      </button>
    </nav>
  );

  return (
    <DashboardShell
      contentScrollMode="contained"
      bottomBar={mobileBottomBar}
      sidebar={
        <div ref={brandAnchor} className="hidden h-full md:block">
          <SidebarNav
            brand={brand}
            sections={sidebarSections}
            footerItems={sidebarFooterItems}
            compact={navigationPrefs.collapsed}
            onToggleCompact={handleToggleCompact}
            onItemSelect={handleMenuNavigateById}
          />
        </div>
      }
      topBar={
        <TopBar
          title={titleOverride?.title ?? brandTitle}
          contextLabel={titleOverride?.context ?? currentMenuItem?.parentLabel ?? currentMenuItem?.label ?? session?.username ?? 'Workspace'}
          leadingSlot={
            <>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0 border-[var(--border)] bg-[var(--background)] shadow-sm md:hidden"
                aria-label="Open navigation menu"
                title="Open navigation menu"
                onClick={() => setMobileNavOpen(true)}
              >
                <Menu className="h-5 w-5" aria-hidden />
              </Button>
              <div className="hidden md:block">
                <PortalShellScopeSelector
                  triggerLabel={scopeTriggerLabel}
                  triggerBadgeLabel={scopeTriggerBadgeLabel}
                  loading={scopeLoading}
                  emptyMessage="No tenants available."
                  options={scopeOptions}
                  onSelect={handleScopeSelect}
                />
              </div>
            </>
          }
          searchSlot={
            <div ref={commandAnchor}>
              <PortalShellSearchTrigger
                value={commandQuery}
                placeholder="Search or type /"
                shortcutLabel="⌘K"
                ariaLabel="Search"
                variant="inline"
                onValueChange={setCommandQuery}
                onActivate={() => handleCommandOpenChange(true)}
              />
            </div>
          }
          actions={topBarActions}
          onAction={handleTopBarAction}
          profileSlot={
            <div className="flex items-center gap-2">
              <div className="md:hidden">
                <PortalShellSearchTrigger
                  value={commandQuery}
                  ariaLabel="Search"
                  variant="icon"
                  onActivate={() => handleCommandOpenChange(true)}
                />
              </div>
              <div ref={quickActionsAnchor}>
                <PortalShellQuickActions
                  items={quickActionItems}
                  triggerAriaLabel="Quick actions"
                  onAction={handleQuickAction}
                />
              </div>
              <UserProfileMenu
                avatarLabel={avatarLabel}
                username={session?.username ?? undefined}
                email={session?.email ?? undefined}
                onLogout={logout}
                renderContent={profileMenuContent}
              />
            </div>
          }
        />
      }
    >
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" size="sm" className="w-64 max-w-[calc(100vw-1.5rem)] p-0 md:hidden">
          <SheetHeader className="sr-only">
            <SheetTitle>Main menu</SheetTitle>
          </SheetHeader>
          <SidebarNav
            brand={brand}
            sections={sidebarSections}
            footerItems={sidebarFooterItems}
            className="h-full w-full border-r-0"
            onItemSelect={handleMenuNavigateById}
          />
        </SheetContent>
      </Sheet>
      <PortalShellCommandPalette
        open={commandOpen}
        query={commandQuery}
        inputPlaceholder="Type a command or search..."
        emptyMessage="No results found."
        groups={commandGroups}
        onOpenChange={handleCommandOpenChange}
        onQueryChange={setCommandQuery}
        onSelect={handleCommandSelect}
      />
      <div ref={contentAnchor} className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
        <ContentFrame
          className="min-h-0 flex-1 overflow-hidden"
          layout="canvas"
          padded={false}
          bodyClassName="h-full min-h-0 overflow-hidden"
        >
          <div className="flex h-full min-h-0 flex-col">
            <div className="shrink-0 border-b border-border/60 bg-card/70 px-3 py-2 md:hidden">
              <PortalShellScopeSelector
                triggerLabel={scopeTriggerLabel}
                triggerBadgeLabel={scopeTriggerBadgeLabel}
                loading={scopeLoading}
                emptyMessage="No tenants available."
                options={scopeOptions}
                fullWidth
                onSelect={handleScopeSelect}
              />
            </div>
            <div data-portal-workspace className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {children}
            </div>
            {footer ? <div className="mt-6 shrink-0">{footer}</div> : null}
          </div>
        </ContentFrame>
      </div>
    </DashboardShell>
  );
}

function buildInitials(source: string): string {
  if (!source) {
    return 'U';
  }
  const parts = source.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '';
  const second = parts[1]?.charAt(0) ?? (parts[0]?.charAt(1) ?? '');
  const initials = `${first}${second}`.toUpperCase();
  return initials || 'U';
}

function renderMenuIcon(icon?: string): ReactNode {
  if (!icon) return null;
  
  const iconProps = { className: "h-4 w-4" };

  switch (icon) {
    case 'shopping-bag': return <ShoppingBag {...iconProps} />;
    case 'credit-card': return <CreditCard {...iconProps} />;
    case 'briefcase': return <Briefcase {...iconProps} />;
    case 'book-open': return <BookOpen {...iconProps} />;
    case 'shield-check': return <ShieldCheck {...iconProps} />;
    case 'send': return <Send {...iconProps} />;
    case 'dollar-sign': return <DollarSign {...iconProps} />;
    case 'user-check': return <UserCheck {...iconProps} />;
    case 'server': return <Server {...iconProps} />;
    case 'key': return <Key {...iconProps} />;
    case 'terminal': return <Terminal {...iconProps} />;
    case 'map': return <Map {...iconProps} />;
    case 'database': return <Database {...iconProps} />;
    case 'palette': return <Palette {...iconProps} />;
    case 'compass': return <Compass {...iconProps} />;
    case 'house': return <House {...iconProps} />;
    case 'image': return <Image {...iconProps} />;
    case 'message-circle': return <MessageCircle {...iconProps} />;
    case 'type': return <Type {...iconProps} />;
    case 'trending-up': return <TrendingUp {...iconProps} />;
    case 'file-text': return <FileText {...iconProps} />;
    case 'package': return <Package {...iconProps} />;
    case 'percent': return <Percent {...iconProps} />;
    case 'shopping-cart': return <ShoppingCart {...iconProps} />;
    case 'settings': return <Settings {...iconProps} />;
    case 'log-out': return <LogOut {...iconProps} />;
    case 'receipt': return <Receipt {...iconProps} />;
    case 'ticket': return <Ticket {...iconProps} />;
    case 'activity': return <Activity {...iconProps} />;
    case 'network': return <Network {...iconProps} />;
    case 'git-merge': return <GitMerge {...iconProps} />;
    case 'grid': return <Grid2x2 {...iconProps} />;
    case 'cpu': return <Cpu {...iconProps} />;
    case 'zap': return <Zap {...iconProps} />;
    case 'map-pin': return <MapPin {...iconProps} />;
    case 'lock': return <Lock {...iconProps} />;
    case 'layers': return <Layers {...iconProps} />;
    case 'users': return <Users {...iconProps} />;
    case 'wifi': return <Wifi {...iconProps} />;
    case 'link': return <Link {...iconProps} />;
    default:
      return (
        <span className="material-icons text-[18px] leading-none" aria-hidden>
          {icon}
        </span>
      );
  }
}

function routeMatches(currentPath: string, route?: string): boolean {
  if (!route) {
    return false;
  }
  if (route === '/') {
    return currentPath === '/';
  }
  return currentPath === route || currentPath.startsWith(`${route}/`);
}

function flattenMenuItems(items: AppMenuItem[], parentLabel?: string | null): FlattenedMenuItem[] {
  return items.flatMap(item => {
    const current: FlattenedMenuItem[] = item.route ? [{ ...item, parentLabel }] : [];
    const children = item.children ? flattenMenuItems(item.children, item.label ?? parentLabel ?? null) : [];
    return [...current, ...children];
  });
}

function findBestMenuMatch(items: FlattenedMenuItem[], currentPath: string): FlattenedMenuItem | null {
  let best: FlattenedMenuItem | null = null;
  for (const item of items) {
    if (!routeMatches(currentPath, item.route)) {
      continue;
    }
    if (!best || (item.route?.length ?? 0) > (best.route?.length ?? 0)) {
      best = item;
    }
  }
  return best;
}

function toShellNavItem(item: AppMenuItem, currentPath: string): DashboardShellNavItem {
  const children = item.children?.map(child => toShellNavItem(child, currentPath));
  const childActive = children?.some(child => child.active);
  const hasChildren = Boolean(children?.length);

  return {
    id: item.id,
    label: item.label,
    icon: renderMenuIcon(item.icon),
    badge: null,
    hint: item.description ?? null,
    active: routeMatches(currentPath, item.route) || childActive,
    collapsible: hasChildren,
    defaultExpanded: childActive,
    children: hasChildren ? children : undefined
  };
}

function buildSidebarSections(
  sideMenu: MenuItem[],
  favoriteItems: AppMenuItem[],
  currentPath: string
): DashboardShellNavSection[] {
  const sections: DashboardShellNavSection[] = [];

  if (favoriteItems.length > 0) {
    sections.push({
      id: 'favorites',
      label: 'Favorites',
      items: favoriteItems.map(item => toShellNavItem(item, currentPath))
    });
  }

  const primaryItems: DashboardShellNavItem[] = [];
  sideMenu.forEach(item => {
    if (item.id === 'dashboard.settings') {
      return;
    }
    if (item.children?.length || item.route) {
      primaryItems.push(toShellNavItem(item, currentPath));
    }
  });

  if (primaryItems.length > 0) {
    sections.push({
      id: 'primary',
      label: 'Workspace',
      items: primaryItems
    });
  }

  return sections;
}

function buildSidebarFooterItems(
  sideMenu: MenuItem[],
  currentPath: string
): DashboardShellFooterAction[] {
  const settingsItem = sideMenu.find(item => item.id === 'dashboard.settings');
  const settingsChildren = settingsItem?.children?.filter(
    (item): item is AppMenuItem & { route: string } => Boolean(item.route)
  ) ?? [];
  if (settingsChildren.length === 0) {
    return [];
  }

  const activeItem = findBestMenuMatch(settingsChildren, currentPath);
  return settingsChildren.map(item => ({
    id: item.id,
    label: item.label,
    icon: renderMenuIcon(item.icon),
    hint: item.description ?? null,
    active: item.id === activeItem?.id
  }));
}

function getScopeErrorMessage(error: unknown, fallback: string) {
  const raw = error instanceof Error ? error.message : String(error ?? fallback);
  try {
    const parsed = JSON.parse(raw) as { detail?: string; title?: string };
    return parsed.detail ?? parsed.title ?? fallback;
  } catch {
    return raw || fallback;
  }
}
